import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BodyWrapper } from '@app/ui/pages/landing-page/body/body.styles';
import { Header } from '@app/ui/pages/landing-page/header/header.component';
import {
  BreakLine,
  CustomSlider,
} from '@app/ui/pages/landing-page/landing-page.styles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { SlideItem } from '@app/ui/pages/landing-page/slides/internet-slide/internet-slide.component';
// @ts-ignore
import backgroundImage1 from '@app/assets/okt-main-slider-1.png';

// @ts-ignore
import backgroundImageBannerTv from '@app/assets/okt-banner-tv.png';

import { BrList } from '@app/ui/pages/landing-page/br-list/br-list.component';
import { RequestForm } from '@app/ui/pages/landing-page/request-form/request-form.component';
import { mainTheme } from '@app/ui/main.theme';
import { ThemeProvider } from '@mui/material/styles';
import { Footer } from '@app/ui/pages/landing-page/footer/footer.component';
import { LayoutLandingPage } from './body/body.component';
import { LoginModal } from '@app/ui/components/auth/LoginModal';
import { PaymentModal } from '@app/ui/components/payment/PaymentModal';
import { useAuth } from '@app/hooks/useAuth';
import { parsePaymentPrefill, PaymentPrefill } from '@app/utils/paymentPrefill';

/** Query-параметры предзаполнения оплаты, которые очищаются при закрытии окна */
const PAYMENT_QUERY_PARAMS = ['pay', 'accountNumber', 'amount', 'annual'] as const;

/**
 * Главная страница (лендинг).
 *
 * Поддерживает ссылки от Telegram-бота вида
 * `/?pay=1&accountNumber=12345678&amount=500&annual=1`: при наличии таких
 * параметров автоматически открывает модальное окно оплаты с предзаполнением,
 * а при закрытии окна удаляет платёжные параметры из адресной строки
 * (остальные query-параметры сохраняются).
 */
export const LandingPage = () => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 6000,
  };

  const requestFormRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Хук авторизации
  const { isAuthenticated, user, logout } = useAuth();
  
  // Состояния для модальных окон
  const [loginOpen, setLoginOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Предзаполнение оплаты из query-параметров (ссылка из Telegram-бота)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPrefill = useMemo(() => parsePaymentPrefill(searchParams), [searchParams]);
  // Предзаполнение, пришедшее из URL. Хранится отдельно, чтобы форма не
  // сбрасывалась в момент очистки параметров при закрытии окна.
  const [paymentPrefill, setPaymentPrefill] = useState<PaymentPrefill | undefined>(undefined);

  // Автоматически открываем окно оплаты, если URL этого требует.
  // После закрытия параметры очищаются, поэтому повторного открытия не происходит.
  useEffect(() => {
    if (urlPrefill.open) {
      setPaymentPrefill(urlPrefill);
      setPaymentOpen(true);
    }
  }, [urlPrefill]);

  /**
   * Закрывает окно оплаты и удаляет платёжные параметры из URL
   * (с заменой записи истории, прочие параметры сохраняются).
   */
  const handlePaymentClose = useCallback(() => {
    setPaymentOpen(false);

    if (PAYMENT_QUERY_PARAMS.some((key) => searchParams.has(key))) {
      const next = new URLSearchParams(searchParams);
      PAYMENT_QUERY_PARAMS.forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const scrollToRequestForm = () => {
    requestFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // Обработчики для модальных окон
  const handleLoginClick = () => {
    setLoginOpen(true);
  };
  
  const handlePaymentClick = () => {
    // Ручное открытие (кнопка «Оплатить») — без предзаполнения из URL
    setPaymentPrefill(undefined);
    setPaymentOpen(true);
  };
  
  const handleProfileClick = () => {
    navigate('/lk');
  };
  
  const handleLogoutClick = () => {
    // Используем функцию logout из хука useAuth
    logout();
  };
  
  const handleLoginSuccess = () => {
    setLoginOpen(false);
  };

  return (
    <ThemeProvider theme={mainTheme}>
      <LayoutLandingPage>
        <BodyWrapper>
          <Header 
            onRequestClick={scrollToRequestForm}
            isAuthenticated={isAuthenticated}
            userName={user?.fullName}
            userPhone={user?.phone}
            onLoginClick={handleLoginClick}
            onPaymentClick={handlePaymentClick}
            onProfileClick={handleProfileClick}
            onLogoutClick={handleLogoutClick}
          />
        </BodyWrapper>
        <BreakLine />
        <CustomSlider {...settings}>
          <SlideItem
            caption='Интернет до 1000 Мбит/c по оптической линии в многоквартирный дом или до 100 Мбит/c по радиоканалу в загородном доме!'
            description='Подключайтесь без затрат! В многоквартирных домах подключаем бесплатно — все расходы берем на себя.'
            buttonValue='Подключиться'
            backgroundImg={backgroundImage1}
            onClick={scrollToRequestForm}
          />
          <SlideItem
            caption='IP TV: 200+ каналов, HD качество, тематические пакеты — телевидение, каким оно должно быть!'
            description='Для работы телевидения необходим подключенный интернет.'
            buttonValue='Подключиться'
            backgroundImg={backgroundImageBannerTv}
            onClick={scrollToRequestForm}
          />
        </CustomSlider>
        <BodyWrapper>
          <BrList />
        </BodyWrapper>
        <BodyWrapper ref={requestFormRef}>
          <RequestForm />
        </BodyWrapper>
      </LayoutLandingPage>
      <Footer />
      
      {/* Модальное окно авторизации */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={handleLoginSuccess}
      />
      
      {/* Модальное окно оплаты */}
      <PaymentModal
        open={paymentOpen}
        onClose={handlePaymentClose}
        isAuthenticated={isAuthenticated}
        userEmail={user?.email}
        prefill={paymentPrefill}
      />
    </ThemeProvider>
  );
};
